/**
 * Shared helpers for the configurable "Shortcuts" feature (quick filters).
 *
 * A shortcut stores a compound filter as native filter JSON, e.g.
 *   { "modality":["CT","MR"], "pacs_status":["DRAFTED"],
 *     "po_his_status":["CONFIRMED"], "center":["smj"],
 *     "date_range": { "start": 2, "end": 0 } }
 * where date_range.start / end are "days ago" offsets (same semantics as the
 * legacy quick_filters qf_date_start / qf_date_end).
 *
 * Everything except `date_range` maps 1:1 onto the PACS/Worklist filter state
 * keys, so applying a shortcut is just setFilters(...) + setDateRange(...).
 */
import dayjs from 'dayjs';

// Fired on window whenever the user changes their shortcuts (enable/create/edit/
// delete) so the open PACS/Worklist chip rows can refetch.
export const SHORTCUTS_UPDATED_EVENT = 'shortcuts-updated';

// Report status (pacs_status) options — mirrors the non-doc statusOptions used
// in the PACS filter form.
export const REPORT_STATUS_OPTIONS = [
  { label: 'SCANNED', value: 'SCANNED' },
  { label: 'DRAFTED', value: 'DRAFTED' },
  { label: 'REVIEWED', value: 'REVIEWED' },
  { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
  { label: 'NOT FOR REPORTING', value: 'NOT_FOR_REPORTING' },
];

// Relative date-range presets. `value` is the serialized key stored on the
// shortcut; null start means "any date" (no date filter).
export const DATE_RANGE_PRESETS = [
  { label: 'Any date', value: 'ANY', start: null, end: null },
  { label: 'Today', value: 'L0D', start: 0, end: 0 },
  { label: 'Yesterday', value: 'Y', start: 1, end: 1 },
  { label: 'Last 2 days', value: 'L2D', start: 2, end: 0 },
  { label: 'Last 3 days', value: 'L3D', start: 3, end: 0 },
  { label: 'Last 7 days', value: 'L7D', start: 7, end: 0 },
  { label: 'Last 30 days', value: 'L30D', start: 30, end: 0 },
];

export const matchDatePreset = (dateRange) => {
  if (!dateRange || dateRange.start === null || dateRange.start === undefined) {
    return DATE_RANGE_PRESETS[0];
  }
  return (
    DATE_RANGE_PRESETS.find(
      p => p.start === Number(dateRange.start) && p.end === Number(dateRange.end)
    ) || null
  );
};

export const parseShortcut = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};

const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/**
 * Split a shortcut's JSON into { filters, dateRange } ready to drop into the
 * page filter state. dateRange is a [dayjs, dayjs] tuple or null.
 */
export const shortcutToFilterAndRange = (raw) => {
  const obj = parseShortcut(raw);
  const { date_range, ...rest } = obj || {};

  const filters = {};
  Object.keys(rest).forEach((k) => {
    if (!isEmpty(rest[k])) filters[k] = rest[k];
  });

  let dateRange = null;
  if (date_range && date_range.start !== null && date_range.start !== undefined) {
    const to = dayjs().subtract(Number(date_range.end ?? 0), 'days').hour(23).minute(59).second(0);
    const from = dayjs().subtract(Number(date_range.start ?? 0), 'days').hour(0).minute(0).second(0);
    dateRange = [from, to];
  }
  return { filters, dateRange };
};

const joinVal = (v) => (Array.isArray(v) ? v.join(', ') : v);

/** Human-readable one-line summary of a shortcut (admin table / chip tooltip). */
export const summarizeShortcut = (raw) => {
  const obj = parseShortcut(raw);
  const parts = [];
  if (!isEmpty(obj.modality)) parts.push(`Modality: ${joinVal(obj.modality)}`);
  if (!isEmpty(obj.pacs_status)) parts.push(`Status: ${joinVal(obj.pacs_status)}`);
  if (!isEmpty(obj.po_his_status)) parts.push(`HIS: ${joinVal(obj.po_his_status)}`);
  if (!isEmpty(obj.center)) parts.push(`Site: ${joinVal(obj.center)}`);
  if (!isEmpty(obj.pat_name)) parts.push(`Name: ${obj.pat_name}`);
  if (!isEmpty(obj.po_acc_no)) parts.push(`Acc: ${obj.po_acc_no}`);
  if (!isEmpty(obj.po_ref_doc)) parts.push(`Ref: ${obj.po_ref_doc}`);
  if (!isEmpty(obj.po_body_part)) parts.push(`Body: ${obj.po_body_part}`);
  if (obj.show_emergency) parts.push('Emergency');
  if (obj.show_rereported) parts.push('Re-reported');
  if (obj.show_mlc) parts.push('MLC');
  if (obj.date_range && obj.date_range.start !== null && obj.date_range.start !== undefined) {
    const preset = matchDatePreset(obj.date_range);
    parts.push(preset ? preset.label : `Date: ${obj.date_range.start}-${obj.date_range.end}d ago`);
  }
  return parts.join(' · ') || 'No filters';
};

/**
 * Merge a /get-my-shortcuts response into one unified chip list:
 * enabled master shortcuts followed by the user's own (pinned) shortcuts.
 */
export const mergeShortcuts = (data) => {
  const masters = (data?.masters || [])
    .filter((m) => m.enabled)
    .map((m) => ({
      key: `m-${m.qf_id}`,
      name: m.qf_name,
      source: 'master',
      filterRaw: m.qf_filter_json || null,
      qf_id: m.qf_id,
      // legacy single-key fields (used when filterRaw is null)
      qf_type: m.qf_type,
      qf_key: m.qf_key,
      qf_value: m.qf_value,
      qf_date_start: m.qf_date_start,
      qf_date_end: m.qf_date_end,
    }));

  const mine = (data?.mine || [])
    .filter((u) => u.uf_pinned !== false)
    .map((u) => ({
      key: `u-${u.uf_id}`,
      name: u.uf_name,
      source: 'mine',
      filterRaw: u.uf_filter_json || null,
      uf_id: u.uf_id,
    }));

  return [...masters, ...mine];
};

/**
 * Fixed, non-editable quick-access chips for technicians on the Orders page
 * (T006). Technicians don't have the configurable Shortcuts feature (no
 * master/per-user rows), so these are built locally instead of coming from
 * /get-my-shortcuts. Orders-only: PACS is studies-based and isn't supposed to
 * surface PENDING/NEW (not-yet-arrived) cases at all, so it doesn't get these.
 */
export const getTechnicianFixedShortcuts = () => [
  {
    key: 'fixed-yesterday',
    name: 'Yesterday',
    source: 'fixed',
    filterRaw: { date_range: { start: 1, end: 1 } },
  },
  {
    key: 'fixed-today',
    name: 'Today',
    source: 'fixed',
    filterRaw: { date_range: { start: 0, end: 0 } },
  },
  {
    key: 'fixed-yet-to-arrive',
    name: "Today's yet to arrive",
    source: 'fixed',
    // NEW-status (stored value 'PENDING' per T005) orders requested today.
    filterRaw: { date_range: { start: 0, end: 0 }, pacs_status: ['PENDING'] },
  },
];

const valuesEqual = (a, b) => {
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    const sb = new Set(b);
    return a.every((x) => sb.has(x));
  }
  return a === b;
};

/**
 * Whether a shortcut's filters are currently applied (for chip active state).
 * Date range is intentionally excluded — it's transient.
 */
export const isShortcutActive = (sc, currentFilters = {}) => {
  if (!sc.filterRaw) {
    if (sc.qf_type === 'TOGGLE') return !!currentFilters[sc.qf_key];
    if (sc.qf_type === 'NA') return valuesEqual(currentFilters[sc.qf_key], sc.qf_value);
    return false;
  }
  const { filters } = shortcutToFilterAndRange(sc.filterRaw);
  const keys = Object.keys(filters);
  if (keys.length === 0) return false;
  return keys.every((k) => valuesEqual(filters[k], currentFilters[k]));
};
